import { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { IconInfoCircle } from '@tabler/icons-react'
import { Checkbox, Tooltip } from 'antd'

import { Role } from '../adminUsers/roles'
import { GET_PUBLIC_PLATFORMACTION_SETTINGS } from '../settings/graphQL'

import { Field, FormFieldValues } from './actionHooks'
import { ACTION_STATUS } from './constants'
import { prioritiesToKeyValueOptions, typesToKeyValueOptions } from './mappers'

import { AssigneeSelect } from '@/components/form-components/AssigneeSelect'
import { AttachmentUploader } from '@/components/form-components/AttachmentUploader'
import {
  KeyValue,
  KeyValueDropdown,
} from '@/components/form-components/dropdowns/KeyValueDropdown'
import { ProjectLocationDropdown } from '@/components/form-components/dropdowns/ProjectLocation/ProjectLocationDropdown'
import { DatePicker } from '@/components/shared-components/BUI/DatePicker'
import { Form } from '@/components/shared-components/BUI/Form'
import { Input, TextArea } from '@/components/shared-components/BUI/Input'
import { GenericProjectSelector } from '@/components/shared-components/Filters/GenericProjectSelector'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { useUser } from '@/context/userContext'
import { useHasRoles } from '@/hooks/useHasRoles'
import { isProjectSelected } from '@/utils/index'

import {
  AttachmentType,
  PublicPlatformActionSettingsUiQuery,
  PublicPlatformActionSettingsUiQueryVariables,
  Resource,
} from 'types/api.graphql'

const rules = {
  status: [{ required: true, message: 'Please select a status' }],
}

type Props = {
  isPublic?: boolean
  builderId: string
  showTitle?: boolean
  showDescription?: boolean
  contextualDate?: string
  disableRequireAttachments?: boolean
}

export const ActionFormItems: React.FC<Props> = ({
  isPublic = false,
  builderId,
  showTitle = true,
  showDescription = true,
  contextualDate,
  disableRequireAttachments = false,
}) => {
  const form = Form.useFormInstance<FormFieldValues>()

  const { projects } = useUser() || {}
  const isWorker = useHasRoles(Role.Worker)

  const { data: settingsData } = useQuery<
    PublicPlatformActionSettingsUiQuery,
    PublicPlatformActionSettingsUiQueryVariables
  >(GET_PUBLIC_PLATFORMACTION_SETTINGS, { variables: { builderId } })

  const types = useMemo<Record<string, KeyValue>>(() => {
    const initialType = form.getFieldValue(Field.Type) // if the current type has since been removed as an option, we want to still show it

    return {
      ...(initialType && { [initialType]: { label: initialType } }),
      ...typesToKeyValueOptions(
        settingsData?.publicPlatformActionSettings?.types
      ),
    }
  }, [settingsData?.publicPlatformActionSettings?.types, form])

  const priorities = useMemo<Record<string, KeyValue>>(() => {
    return prioritiesToKeyValueOptions(
      settingsData?.publicPlatformActionSettings?.priorities
    )
  }, [settingsData?.publicPlatformActionSettings?.priorities])

  return (
    <>
      {types && (
        <Form.Item name={Field.Type} label="Type">
          <KeyValueDropdown
            options={types}
            noValueLabel="Select an action type"
            showCaret
          />
        </Form.Item>
      )}

      {showTitle && (
        <Form.Item name={Field.Title} label="Title">
          <Input />
        </Form.Item>
      )}

      {showDescription && (
        <Form.Item name={Field.Description} label="Description">
          <TextArea />
        </Form.Item>
      )}

      <Form.Item name={Field.Status} label="Status" rules={rules.status}>
        <KeyValueDropdown
          options={ACTION_STATUS}
          noValueLabel="Status"
          showCaret
        />
      </Form.Item>

      {Object.keys(priorities)?.length > 1 && (
        <Form.Item name={Field.PriorityType} label="Priority">
          <KeyValueDropdown
            options={priorities}
            noValueLabel="Priority"
            showCaret
            clear={{ label: 'No priority' }}
          />
        </Form.Item>
      )}

      <Form.Item name={Field.DueDate} label="Due Date">
        <DatePicker format="L" />
      </Form.Item>

      {!isPublic && (
        <Form.Item dependencies={[Field.Project]} noStyle>
          {({ getFieldValue }) => {
            const projectId = getFieldValue(Field.Project)?.id

            return (
              <Form.Item name={Field.Assignees} label="Assignees">
                <AssigneeSelect
                  key={projectId} // force rerender when project changes
                  project={
                    isProjectSelected({ id: projectId })
                      ? projects.find((p) => p.id === projectId)
                      : undefined
                  }
                />
              </Form.Item>
            )
          }}
        </Form.Item>
      )}

      {!!projects?.length && (
        <Form.Item name={Field.Project} label="Project">
          <GenericProjectSelector
            projects={projects}
            includeBuilder={false}
            multi={false}
            clearable
            allowUnlistedSelection
          />
        </Form.Item>
      )}

      {!isPublic && (
        <Form.Item dependencies={[Field.Project]} noStyle>
          {({ getFieldValue }) => (
            <Form.Item label="Location" name={Field.Location}>
              <ProjectLocationDropdown
                showCaret
                projectId={getFieldValue(Field.Project)?.id}
                treeProps={{ defaultExpandAll: true }}
              />
            </Form.Item>
          )}
        </Form.Item>
      )}

      <br />

      <Form.Item dependencies={[Field.Project]} noStyle>
        {({ getFieldValue }) => (
          <Form.Item
            name={Field.Attachments}
            label="Attachments"
            valuePropName="fileList"
          >
            <AttachmentUploader
              attachmentType={AttachmentType.Media}
              attachmentResource={Resource.PlatformAction}
              attachmentSortDate={contextualDate}
              projectId={getFieldValue(Field.Project)?.id}
              isPublic={isPublic}
              multiple
            />
          </Form.Item>
        )}
      </Form.Item>

      {!isWorker && (
        <Form.Item
          name={Field.RequireAttachment}
          valuePropName="checked"
          label=" "
        >
          <Checkbox disabled={disableRequireAttachments}>
            <Flexbox alignItems="center" gap="0.5rem">
              Require attachment to close?
              <Tooltip
                title={
                  <>
                    An attachment is required to be submitted when status is
                    changed from{' '}
                    <strong>{ACTION_STATUS.IN_PROGRESS.label}</strong> to{' '}
                    <strong>{ACTION_STATUS.WAITING_REVIEW.label}</strong>,{' '}
                    <strong>{ACTION_STATUS.COMPLETE.label}</strong>, or{' '}
                    <strong>{ACTION_STATUS.CANT_DO.label}</strong>.
                  </>
                }
              >
                <IconInfoCircle size={16} />
              </Tooltip>
            </Flexbox>
          </Checkbox>
        </Form.Item>
      )}
    </>
  )
}
